import React, { useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { configureStore } from '@reduxjs/toolkit'
import { ThemeProvider } from '@emotion/react'
import { useAuth0 } from '@auth0/auth0-react'
import { HelmetProvider } from 'react-helmet-async'

import { theme } from '@campaignhub/suit-theme'

import { getShareLinkTokenFromUrl } from '@functions/url'

import '@campaignhub/suit-theme/dist/styles.css'
import '@styles/reset.css'

import AuthController from '@sections/Auth/AuthController'

import createRootReducer from '@redux/rootReducer'
import lastModifiedMiddleware from '@redux/middleware/lastModified'
import { setAccessToken } from '@functions/accessToken'

const store = configureStore({
  reducer: createRootReducer(),
  middleware: getDefaultMiddleware => getDefaultMiddleware({ immutableCheck: false }).concat(lastModifiedMiddleware),
})

const otpLogoutAttemptStorageKey = 'contenthouse:otpLogoutAttempt'

const getOtpLoginContext = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const username = urlParams.get('username')
  const otpToken = urlParams.get('otpToken')

  if (!username || !otpToken) {
    return null
  }

  return { username, otpToken }
}

const getOtpLoginAttemptFingerprint = ({ username, otpToken }) => {
  const attemptValue = `${username}:${otpToken}`
  let hash = 0

  for (let index = 0; index < attemptValue.length; index += 1) {
    hash = ((hash << 5) - hash + attemptValue.charCodeAt(index)) | 0
  }

  return `${username}:${hash >>> 0}`
}

function App() {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0()
  const [hasInitiatedOtpLogout, setHasInitiatedOtpLogout] = useState(false)

  const shareLinkToken = getShareLinkTokenFromUrl(window.location.pathname)
  const otpLoginContext = getOtpLoginContext()
  const otpLoginAttemptFingerprint = otpLoginContext ? getOtpLoginAttemptFingerprint(otpLoginContext) : null
  const hasRecordedOtpLogoutAttempt = !!(
    otpLoginAttemptFingerprint
    && window.sessionStorage.getItem(otpLogoutAttemptStorageKey) === otpLoginAttemptFingerprint
  )
  const shouldBlockAuthController = !!(
    otpLoginContext
    && isAuthenticated
    && (!hasRecordedOtpLogoutAttempt || hasInitiatedOtpLogout)
  )

  useEffect(() => {
    if (!otpLoginContext) {
      window.sessionStorage.removeItem(otpLogoutAttemptStorageKey)
      setHasInitiatedOtpLogout(false)

      return
    }

    if (!isAuthenticated) {
      setHasInitiatedOtpLogout(false)

      return
    }

    if (hasRecordedOtpLogoutAttempt) {
      return
    }

    window.sessionStorage.setItem(otpLogoutAttemptStorageKey, otpLoginAttemptFingerprint)
    setHasInitiatedOtpLogout(true)
    logout({ returnTo: window.location.href })

    return undefined
  }, [
    hasRecordedOtpLogoutAttempt,
    isAuthenticated,
    logout,
    otpLoginAttemptFingerprint,
    otpLoginContext,
  ])

  useEffect(() => {
    if (isAuthenticated && !shareLinkToken && !shouldBlockAuthController) {
      setAccessToken(getAccessTokenSilently)
    }
  }, [getAccessTokenSilently, isAuthenticated, shareLinkToken, shouldBlockAuthController])

  return (
    <HelmetProvider>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          {shouldBlockAuthController ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              Logging out...
            </div>
          ) : (
            <AuthController shareLinkToken={shareLinkToken} />
          )}
        </ThemeProvider>
      </Provider>
    </HelmetProvider>
  )
}

export default App
